import { AUTOSCALE_ANIMATION_MS } from "../constants";
import { easeOutProgress, isRunning } from "./transition";

/**
 * Smooth autoscale. The fitted range follows the shown bars directly (the
 * moving last bar already moves it smoothly); it jumps only when other bars
 * come into view, e.g. while the view slides after a new bar. Then the
 * difference between the shown range and the new one fades out over
 * AUTOSCALE_ANIMATION_MS. A snapped frame (gesture, manual scale, new
 * history) applies the range at once.
 */
export const createAutoscaleMotion = () => {
  let shownRange = null;
  let fittedBars = null;
  // { min, max, startTime, duration }: shown minus target when the visible bars changed
  let settling = null;

  return {
    isActive: (now) => isRunning(settling, now),

    /**
     * @param {{ min: number, max: number } | null} targetRange - prices of the visible bars
     * @param {{ first: number, last: number }} visibleBars - their indexes
     * @param {{ isSnapped: boolean, now: number }} params
     * @returns {{ min: number, max: number } | null} range to show
     */
    smooth: (targetRange, visibleBars, { isSnapped, now }) => {
      if (!targetRange) return targetRange;

      const hasOtherBars =
        !fittedBars || fittedBars.first !== visibleBars.first || fittedBars.last !== visibleBars.last;

      fittedBars = { first: visibleBars.first, last: visibleBars.last };

      if (isSnapped || !shownRange) {
        settling = null;
      } else if (hasOtherBars) {
        settling = {
          min: shownRange.min - targetRange.min,
          max: shownRange.max - targetRange.max,
          startTime: now,
          duration: AUTOSCALE_ANIMATION_MS,
        };
      }

      if (settling) {
        const remaining = 1 - easeOutProgress(settling, now);

        shownRange = {
          min: targetRange.min + settling.min * remaining,
          max: targetRange.max + settling.max * remaining,
        };
      } else {
        shownRange = targetRange;
      }

      if (!isRunning(settling, now)) settling = null;

      return shownRange;
    },
  };
};
